// PIAȚA DIN JURUL TERENULUI, PE BRIEF — în coloana din dreapta, lângă temă.
//
// Vânzările din zonă se caută la cerere, când terenul primește o adresă
// (`viewer/cost/piata.ts`: `colecteaza`, `useCautaLaCapatulCitirii`). Omul ajunge
// aici la câteva minute după ce urcă actele, iar o căutare ține de la câteva
// minute la o jumătate de oră; de aceea cardul spune unde a ajuns căutarea și
// cât mai durează, cu cifrele provizorii, și nu-l ține pe loc.
//
// PIAȚA ÎNLOCUIEȘTE IMPLICITUL, NU ALEGEREA OMULUI (Stefan, 31.10.2026: «dacă se
// poate calcula înainte să ajung la pagina de brief, să-l aplice deja automat»).
// Cât tema n-are un mix ales de om, recomandarea unei căutări încheiate se scrie
// singură în temă, cu urma ei (`Tema.piata`), deci ajunge la motor oricum ar
// porni generarea. Spre deosebire de presetul regimului, care se scrie abia la
// generare: acolo o valoare scrisă la deschidere ar fi părut aleasă de om, aici
// eticheta spune «din piața zonei». Tema schimbată de om rămâne a lui, iar
// cardul îi oferă «Aplică în temă», oricând există o recomandare, și cât se caută.
// «Vezi detalii» deschide toată cercetarea.

import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { type Cheie, numar, t, useT } from "../i18n";
import CardPiata from "../viewer/cost/CardPiata";
import PanouPiata from "../viewer/cost/PanouPiata";
import { type Colectare, type Profil, type Recomandare, colecteaza, getPiata, getProfil, inLucru }
  from "../viewer/cost/piata";
import type { Calitate, Tema, TipApartament } from "./dosarclient";

export { catMaiDureaza } from "../viewer/cost/CardPiata";

// Cât se caută, starea se cere din nou la atât; ea e ieftină (`?oferte=0`).
const SONDAJ_MS = 10_000;
// Profilul, cât se caută: cifrele provizorii cresc odată cu paginile citite.
const SONDAJ_PROFIL_MS = 30_000;

// Grupa de camere a recomandării → tipul de apartament al temei. Numele sunt ale
// implicitelor, fiindcă din ele citește motorul câte camere are tipul
// (`control-plane/services/siteplan._camere`).
const TIPURI: Record<Recomandare["mix"][number]["grupa"], Cheie> = {
  "1": "loc.ap.garsoniera", "2": "loc.ap.doua_camere", "3": "loc.ap.trei_camere", "4": "loc.ap.patru_camere",
};
// Clasa de finisaj a pieței → standardul clădirii din temă.
const STANDARD: Record<string, string> = { standard: "standard", premium: "premium", luxury: "lux" };
const CAPITOLE: (keyof Calitate)[] = ["compartimentare", "finisaje", "fatada", "hvac", "sanitare", "electrice"];

/** Ce pune în temă recomandarea: mixul cu suprafețele tipice și, dacă piața are
 *  o clasă de finisaj, standardul clădirii (cu capitolele înapoi pe el, ca la
 *  alegerea lui de mână). */
export function temaDinRecomandare(r: Recomandare): Partial<Tema> {
  const structura: TipApartament[] = r.mix.map((m) => ({
    tip: t(TIPURI[m.grupa]), numar: null, procent: m.pct / 100,
    suprafata_utila_mp: m.suprafata == null ? null : Math.round(m.suprafata),
  }));
  const nivel = r.finisaje ? STANDARD[r.finisaje.clasa] : undefined;
  return {
    structura_apartamentelor: structura,
    ...(nivel ? { calitate: { nivel, compartimentare: null, finisaje: null, fatada: null,
                              hvac: null, sanitare: null, electrice: null } } : {}),
  };
}

/** Același mix: aceleași procente și suprafețe, în aceeași ordine. Numele nu se
 *  compară — sunt în limba omului care l-a scris. */
export function aceeasiStructura(a: TipApartament[] | null | undefined, b: TipApartament[] | null | undefined) {
  const x = a ?? [], y = b ?? [];
  return x.length === y.length && x.every((t1, i) =>
    Math.round((t1.procent ?? 0) * 100) === Math.round((y[i].procent ?? 0) * 100)
    && (t1.suprafata_utila_mp ?? null) === (y[i].suprafata_utila_mp ?? null));
}

/** CE SCRIE PIAȚA SINGURĂ ÎN TEMĂ, sau `null`. Numai dintr-o căutare încheiată,
 *  cu recomandare; mixul, cât tema n-are unul ales de om (gol, sau exact cel scris
 *  ultima dată de piață); standardul, cât nivelul n-a fost ales (sau e cel scris
 *  de piață) și niciun capitol nu e altfel. Urma (`piata`) ține ce s-a scris. */
export function temaDinPiata(tema: Tema, profil: Profil | null): Partial<Tema> | null {
  const r = profil?.recomandare;
  const col = profil?.colectare;
  if (!r || !col || col.stare !== "gata") return null;
  const urma = tema.piata ?? null;
  const p = temaDinRecomandare(r);
  const mixLiber = !tema.structura_apartamentelor?.length
    || (urma != null && aceeasiStructura(tema.structura_apartamentelor, urma.structura));
  const cal = tema.calitate;
  const nivelLiber = (!cal?.nivel || (urma?.nivel != null && cal.nivel === urma.nivel))
    && CAPITOLE.every((c) => cal?.[c] == null);
  if (!mixLiber && !nivelLiber) return null;
  const out: Partial<Tema> = {};
  if (mixLiber && !aceeasiStructura(tema.structura_apartamentelor, p.structura_apartamentelor)) {
    out.structura_apartamentelor = p.structura_apartamentelor;
  }
  if (nivelLiber && p.calitate && cal?.nivel !== p.calitate.nivel) out.calitate = p.calitate;
  if (!Object.keys(out).length && urma?.colectare === col.id) return null;
  return {
    ...out,
    piata: {
      colectare: col.id,
      structura: mixLiber ? p.structura_apartamentelor ?? [] : urma?.structura ?? [],
      nivel: nivelLiber ? p.calitate?.nivel ?? urma?.nivel ?? null : urma?.nivel ?? null,
    },
  };
}

/** CE SCRIE BUTONUL «Aplică în temă»: procentele și suprafețele, cu urma lor.
 *  Standardul nu: ar șterge și alegerile omului pe capitole, iar el a cerut mixul.
 *  Urma face ca o căutare care se încheie să înlocuiască cifrele provizorii, cât
 *  tema a rămas cum a pus-o butonul (`temaDinPiata`). */
export function temaDinButon(tema: Tema, r: Recomandare, colectare: number): Partial<Tema> {
  const structura = temaDinRecomandare(r).structura_apartamentelor ?? [];
  return { structura_apartamentelor: structura,
           piata: { colectare, structura, nivel: tema.piata?.nivel ?? null } };
}

/** Tema e încă a pieței: mixul e exact cel scris ultima dată de ea. */
export const mixDinPiata = (tema: Tema) =>
  !!tema.piata && !!tema.structura_apartamentelor?.length
  && aceeasiStructura(tema.structura_apartamentelor, tema.piata.structura);

/** STAREA CĂUTĂRII ȘI PROFILUL ZONEI, pentru brief: starea la 10 s cât se caută,
 *  profilul la fiecare schimbare a colectării (și la 30 s cât se caută). Un
 *  singur sondaj pe pagină: tema îl folosește la aplicare, cardul la desen. */
export function useStareaPietei(projectId: string) {
  // `undefined` = încă nu se știe; `null` = nicio căutare pentru proiect
  const [colectare, setColectare] = useState<Colectare | null | undefined>(undefined);
  const [profil, setProfil] = useState<Profil | null>(null);

  const citeste = useCallback(() => getPiata(projectId, false)
    .then((p) => setColectare(p.colectare ?? null)).catch(() => setColectare(null)), [projectId]);
  const citesteProfil = useCallback(() => getProfil(projectId)
    .then((p) => setProfil(p?.esantion ? p : null)).catch(() => setProfil(null)), [projectId]);

  useEffect(() => { void citeste(); }, [citeste]);
  const seCauta = inLucru(colectare);
  useEffect(() => {
    if (!seCauta) return;
    const ceas = setInterval(() => void citeste(), SONDAJ_MS);
    const ceasProfil = setInterval(() => void citesteProfil(), SONDAJ_PROFIL_MS);
    return () => { clearInterval(ceas); clearInterval(ceasProfil); };
  }, [seCauta, citeste, citesteProfil]);
  const cheie = colectare ? `${colectare.id}|${colectare.stare}` : null;
  useEffect(() => { if (cheie) void citesteProfil(); }, [cheie, citesteProfil]);

  const cauta = useCallback(() => void colecteaza(projectId).then((r) => setColectare(r.colectare))
    .catch(() => undefined), [projectId]);
  return { colectare, profil, cauta };
}

// Cu suprafața: butonul o scrie și pe ea, iar două mixuri care diferă numai la
// suprafețe s-ar citi altfel la fel, cu butonul aprins fără vreun motiv vizibil.
const mixScurt = (s: TipApartament[]) =>
  s.map((a) => `${numar(Math.round((a.procent ?? 0) * 100))} % ${a.tip}`
    + (a.suprafata_utila_mp == null ? "" : ` ${numar(a.suprafata_utila_mp)} m²`)).join(" · ");

export default function PiataPeBrief({ projectId, areAdresa, potScrie, tema, piata, laAplica }: {
  projectId: string;
  /** terenul are un punct pe hartă; fără el nu e nimic de căutat */
  areAdresa: boolean;
  potScrie: boolean;
  tema: Tema;
  piata: ReturnType<typeof useStareaPietei>;
  laAplica: (p: Partial<Tema>) => void;
}) {
  useT();
  const [detalii, setDetalii] = useState(false);
  const { colectare: col, profil, cauta } = piata;
  if (col === undefined) return null;
  const r = profil?.recomandare ?? null;
  const gata = profil?.colectare?.stare === "gata";
  const recomandat = r ? temaDinRecomandare(r) : null;
  const egal = !!recomandat && aceeasiStructura(tema.structura_apartamentelor, recomandat.structura_apartamentelor);
  const areDate = !!profil;

  const gol = !areAdresa && col === null ? <p className="cp-nota">{t("cp.fara_punct")}</p>
    : col === null ? (
      <div className="cp-rand">
        <span className="cp-nota">{t("tema.piata.fara_cautare")}</span>
        {potScrie && <button type="button" className="btn btng" onClick={cauta}>{t("tema.piata.cauta")}</button>}
      </div>
    ) : col.stare === "eroare" && !areDate ? (
      <div className="cp-rand">
        <span className="cp-nota">{t("tema.piata.eroare")}</span>
        {potScrie && <button type="button" className="btn btng" onClick={cauta}>{t("tema.piata.reincearca")}</button>}
      </div>
    ) : inLucru(col) && !r ? <p className="cp-nota">{t("cp.asteapta")}</p> : undefined;

  const vezi = areDate && (
    <button type="button" className="btn btng" title={t("cp.detalii_t")} onClick={() => setDetalii(true)}>
      {t("cp.detalii")}</button>
  );
  const jos = !r ? vezi || null : (
    <>
      {gata && egal && (
        <p className="cp-nota">{t(mixDinPiata(tema) ? "cp.aplicat_t" : "cp.egal_t")}</p>
      )}
      {!egal && !!tema.structura_apartamentelor?.length && (
        <div className="cp-dif">
          <span className="cp-mut">{t("cp.tema_ta")}</span><span>{mixScurt(tema.structura_apartamentelor)}</span>
          <span className="cp-mut">{t("cp.piata")}</span><span>{mixScurt(recomandat!.structura_apartamentelor ?? [])}</span>
        </div>
      )}
      {!gata && <p className="cp-nota">{t("cp.asteapta")}</p>}
      <div className="cp-rand">
        {/* Butonul stă mereu pe loc, ca să nu dispară când piața s-a aplicat singură:
            stins și cu semnul când tema are deja cifrele. */}
        {potScrie ? (
          <button type="button" className="btn btnp" disabled={egal}
                  title={egal ? undefined : t(gata ? "tema.piata.foloseste_t" : "cp.aplica_provizoriu_t")}
                  onClick={() => laAplica(temaDinButon(tema, r, profil!.colectare!.id))}>
            {egal ? `✓ ${t("cp.aplicat")}` : t("cp.aplica")}</button>
        ) : gata && egal && <span className="chip c-ok">✓ {t("cp.aplicat")}</span>}
        {vezi}
      </div>
    </>
  );

  return (
    <>
      <CardPiata profil={profil} colectare={col} gol={gol} jos={jos} />
      {detalii && <SertarPiata projectId={projectId} potScrie={potScrie} laInchide={() => setDetalii(false)} />}
    </>
  );
}

/** TOATĂ CERCETAREA, peste brief: fila «Vânzări în zonă» întreagă — profilul,
 *  toate ofertele, fiecare anunț —, fără să pleci de pe temă. Poartă `obs`,
 *  clasa sub care stau tabelele platformei.
 *
 *  Se desenează în rădăcina aplicației (`.ob`, cu variabilele ei), nu pe loc:
 *  coloana lipită a pieței își face propriul strat (`position: sticky`), iar
 *  dinăuntrul lui sertarul ar fi rămas sub bara de sus, oricât de mare i-ar fi
 *  fost `z-index`-ul. */
function SertarPiata({ projectId, potScrie, laInchide }: {
  projectId: string; potScrie: boolean; laInchide: () => void;
}) {
  useT();
  useEffect(() => {
    const tasta = (e: KeyboardEvent) => { if (e.key === "Escape") laInchide(); };
    window.addEventListener("keydown", tasta);
    return () => window.removeEventListener("keydown", tasta);
  }, [laInchide]);
  return createPortal(
    <div className="cp-sertar-fundal" onClick={laInchide}>
      <div className="cp-sertar obs" role="dialog" aria-modal="true" aria-labelledby="cp-sertar-h"
           onClick={(e) => e.stopPropagation()}>
        <div className="cp-sertar-cap">
          <b id="cp-sertar-h">{t("costuri.tab.piata")}</b>
          <span className="cp-sp" />
          <button type="button" className="btn btng" onClick={laInchide} aria-label={t("pz.d.inchide")}>✕</button>
        </div>
        <div className="cp-sertar-corp">
          <PanouPiata projectId={projectId} potScrie={potScrie} />
        </div>
      </div>
    </div>,
    document.querySelector(".ob") ?? document.body,
  );
}
